import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Star, Clock, ChevronRight, X, Sparkles } from 'lucide-react';
import { SmallText, BodyText } from './brand/BrandTypography';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subtle Founding Member Banner
 * Non-intrusive banner that appears at the top of pages
 */
export const FoundingMemberBanner = ({ variant = 'subtle', onDismiss }) => {
    const navigate = useNavigate();
    const [offer, setOffer] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const fetchOffer = async () => {
            // Check if already dismissed this session
            if (sessionStorage.getItem('founding_banner_dismissed')) {
                setDismissed(true);
                return;
            }

            try {
                const response = await fetch(`${API_URL}/api/founding-offer`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.is_active) {
                        setOffer(data);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch founding offer:', err);
            }
        };

        fetchOffer();
    }, []);

    const handleDismiss = () => {
        sessionStorage.setItem('founding_banner_dismissed', 'true');
        setDismissed(true);
        onDismiss?.();
    };

    if (!offer || dismissed) return null;

    if (variant === 'minimal') {
        return (
            <div 
                className="bg-gradient-to-r from-[#1D6B6B]/5 to-[#145555]/5 border-b border-[#1D6B6B]/10"
                data-testid="founding-banner-minimal"
            >
                <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
                    <button 
                        onClick={() => navigate('/pricing')}
                        className="flex items-center gap-2 text-sm text-[#1D6B6B] hover:text-[#145555] transition-colors"
                    >
                        <Sparkles className="w-4 h-4" />
                        <span>Founding Member: <strong>{offer.discount_percent}% off</strong> first year</span>
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    {offer.days_remaining !== undefined && (
                        <SmallText className="text-[#7D8E95]">
                            {offer.days_remaining} days left
                        </SmallText>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div 
            className="relative bg-gradient-to-r from-[#1D6B6B]/8 to-[#145555]/8 border-b border-[#1D6B6B]/15"
            data-testid="founding-banner"
        >
            <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
                {/* Left side - Icon + Text */}
                <button 
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
                >
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center flex-shrink-0">
                        <Gift className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-medium text-[#1D6B6B] truncate">
                            <span className="hidden sm:inline">Founding Member</span>
                            <span className="sm:hidden">Founder</span>
                        </span>
                        <span className="px-1.5 py-0.5 rounded-full bg-[#E08850] text-white text-xs font-bold flex-shrink-0">
                            {offer.discount_percent}% off
                        </span>
                    </div>
                </button>

                {/* Right side - CTA + Dismiss */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                        onClick={() => navigate('/pricing')}
                        className="px-2.5 py-1 rounded-full bg-[#1D6B6B] text-white text-xs font-medium hover:bg-[#145555] transition-colors flex items-center gap-0.5"
                        data-testid="founding-banner-cta"
                    >
                        <span className="hidden sm:inline">See Pricing</span>
                        <span className="sm:hidden">View</span>
                        <ChevronRight className="w-3 h-3" />
                    </button>

                    <button
                        onClick={handleDismiss}
                        className="p-1 rounded-full hover:bg-[#1D6B6B]/10 transition-colors text-[#7D8E95]"
                        aria-label="Dismiss"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Founding Member Badge
 * Small badge shown on user profiles and in the app
 */
export const FoundingMemberBadge = ({ size = 'default', showTooltip = true }) => {
    const sizeClasses = {
        small: 'px-2 py-0.5 text-xs gap-1',
        default: 'px-3 py-1 text-sm gap-1.5',
        large: 'px-4 py-1.5 text-base gap-2'
    };

    const iconSizes = {
        small: 'w-3 h-3',
        default: 'w-4 h-4',
        large: 'w-5 h-5'
    };

    return (
        <div 
            className={`inline-flex items-center rounded-full bg-gradient-to-r from-[#E4D3A3]/30 to-[#FFBB98]/30 border border-[#E4D3A3]/50 text-[#1D6B6B] font-medium ${sizeClasses[size]}`}
            title={showTooltip ? "Founding Member - Thank you for your early support!" : undefined}
            data-testid="founding-member-badge"
        >
            <Star className={`${iconSizes[size]} text-[#E4D3A3] fill-[#E4D3A3]`} />
            <span>Founding Member</span>
        </div>
    );
};

/**
 * Founding Member Card
 * Shown on the pricing page to highlight the offer
 */
export const FoundingMemberCard = ({ offer, compact = false }) => {
    const navigate = useNavigate();

    if (!offer?.is_active) return null;

    if (compact) {
        return (
            <div 
                className="p-4 rounded-2xl bg-gradient-to-br from-[#1D6B6B]/5 to-[#145555]/10 border border-[#1D6B6B]/20"
                data-testid="founding-card-compact"
            >
                <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-5 h-5 text-[#1D6B6B]" />
                    <SmallText className="font-semibold text-[#1D6B6B]">
                        Founding Member Offer
                    </SmallText>
                </div>
                <SmallText className="text-[#7D8E95]">
                    {offer.discount_percent}% off your first year + exclusive perks
                </SmallText>
            </div>
        );
    }

    return (
        <div 
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D6B6B]/10 via-[#1D6B6B]/5 to-[#145555]/10 border border-[#1D6B6B]/20 p-6 md:p-8"
            data-testid="founding-card"
        >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#E4D3A3]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#1D6B6B]/5 rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center">
                            <Gift className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-[#1D6B6B]">
                                {offer.headline || 'Founding Member Offer'}
                            </h3>
                            <SmallText className="text-[#7D8E95]">Limited time only</SmallText>
                        </div>
                    </div>
                    
                    {offer.days_remaining !== undefined && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 border border-[#FFBB98]/30">
                            <Clock className="w-4 h-4 text-[#7D8E95]" />
                            <SmallText className="font-medium text-[#1D6B6B]">
                                {offer.days_remaining} days left
                            </SmallText>
                        </div>
                    )}
                </div>

                <BodyText className="text-[#344648] mb-6">
                    {offer.description || `Join as a Founding Member and receive ${offer.discount_percent}% off your first year, plus exclusive perks.`}
                </BodyText>

                {/* Perks list */}
                {offer.perks?.length > 0 && (
                    <div className="space-y-2 mb-6">
                        {offer.perks.map((perk, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-[#E4D3A3] fill-[#E4D3A3] flex-shrink-0" />
                                <SmallText className="text-[#344648]">{perk}</SmallText>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    onClick={() => navigate('/pricing')}
                    className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white font-medium hover:from-[#145555] hover:to-[#0F4444] transition-all shadow-lg shadow-[#1D6B6B]/20"
                    data-testid="founding-card-cta"
                >
                    Become a Founding Member
                </button>
            </div>
        </div>
    );
};

/**
 * Hook to get founding offer data
 */
export const useFoundingOffer = () => {
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [prices, setPrices] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [offerRes, pricesRes] = await Promise.all([
                    fetch(`${API_URL}/api/founding-offer`),
                    fetch(`${API_URL}/api/founding-offer/prices`)
                ]);

                if (offerRes.ok) {
                    setOffer(await offerRes.json());
                }
                if (pricesRes.ok) {
                    setPrices(await pricesRes.json());
                }
            } catch (err) {
                console.error('Failed to fetch founding offer:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return { offer, prices, loading, isActive: offer?.is_active };
};

export default FoundingMemberBanner;
